// src/modules/ai-agent/ServiceAreaManager.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../layouts/DashboardLayout';
import Modal from '../../components/shared/Modal';
import { getServiceAreas, createServiceArea, updateServiceArea, deleteServiceArea } from './aiAgentService';
import { FaMapMarkerAlt, FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaGlobe, FaCity, FaMailBulk } from 'react-icons/fa';

// ─── Styled Components ──────────────────────────────────────────────────────────
const Container = styled.div`
  padding: 20px;
  max-width: 900px;
`;

const PageTitle = styled.h1`
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const AddButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  &:hover { opacity: 0.9; }
`;

const AreaCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-left: 4px solid ${props => {
    switch(props.areaType) {
      case 'zipcode': return '#2196f3';
      case 'city_radius': return '#4caf50';
      case 'state': return props.theme.colors.primary;
      default: return props.theme.colors.surface;
    }
  }};
`;

const AreaInfo = styled.div`
  flex: 1;
`;

const AreaName = styled.div`
  color: ${props => props.theme.colors.textPrimary};
  font-weight: 500;
  margin-bottom: 4px;
`;

const AreaMeta = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.85rem;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textSecondary};
`;

const ActiveBadge = styled.span`
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 10px;
  background-color: ${props => props.active ? '#4caf5033' : '#fa7f7f33'};
  color: ${props => props.active ? '#4caf50' : '#fa7f7f'};
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.color || props.theme.colors.textSecondary};
  cursor: pointer;
  font-size: 1rem;
  padding: 6px;
  &:hover { color: ${props => props.theme.colors.primary}; }
`;

const FormOverlay = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
  border: 1px solid ${props => props.theme.colors.primary}44;
`;

const FormTitle = styled.h3`
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.cols || '1fr 1fr'};
  gap: 12px;
  margin-bottom: 12px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Label = styled.label`
  display: block;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 4px;
  font-size: 0.85rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface};
  border-radius: 6px;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.9rem;
  box-sizing: border-box;
  &:focus { outline: none; border-color: ${props => props.theme.colors.primary}; }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface};
  border-radius: 6px;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.9rem;
  box-sizing: border-box;
  &:focus { outline: none; border-color: ${props => props.theme.colors.primary}; }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  background-color: ${props => props.variant === 'cancel' ? props.theme.colors.background : props.theme.colors.primary};
  color: ${props => props.variant === 'cancel' ? props.theme.colors.textSecondary : 'white'};
  &:hover { opacity: 0.9; }
`;

const MapContainer = styled.div`
  width: 100%;
  height: 350px;
  border-radius: 8px;
  margin-top: 12px;
  background-color: ${props => props.theme.colors.background};
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RangeInput = styled.input`
  flex: 1;
  accent-color: ${props => props.theme.colors.primary};
`;

const RangeValue = styled.span`
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  min-width: 60px;
`;

const TypeButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const TypeButton = styled.button`
  flex: 1;
  padding: 10px;
  border: 2px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.background};
  border-radius: 8px;
  background-color: ${props => props.active ? props.theme.colors.primary + '22' : props.theme.colors.background};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  transition: all 0.2s;

  svg { font-size: 1.2rem; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: ${props => props.theme.colors.textSecondary};
`;

// ─── Constants ──────────────────────────────────────────────────────────────────
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' }
];

const typeLabel = (t) => {
  switch(t) {
    case 'zipcode': return 'Zipcode';
    case 'city_radius': return 'City + Radius';
    case 'state': return 'Entire State';
    default: return t;
  }
};

const typeIcon = (t) => {
  switch(t) {
    case 'zipcode': return <FaMailBulk />;
    case 'city_radius': return <FaCity />;
    case 'state': return <FaGlobe />;
    default: return null;
  }
};

const emptyForm = { name: '', area_type: 'city_radius', center_lat: null, center_lng: null, radius_miles: 25, zipcode: '', city: '', state: 'MA', is_active: true };

// ─── Component ──────────────────────────────────────────────────────────────────
const ServiceAreaManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showModal, setShowModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const autocompleteInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await getServiceAreas();
      setItems(data);
    } catch (err) {
      console.error('Error loading service areas:', err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Google Maps ────────────────────────────────────────────────────
  const loadGoogleMaps = useCallback((callback) => {
    if (window.google && window.google.maps) {
      callback();
      return;
    }
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) {
      existing.addEventListener('load', callback);
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = callback;
    document.body.appendChild(script);
  }, []);

  const initAutocomplete = useCallback(() => {
    if (!window.google || !autocompleteInputRef.current || autocompleteRef.current) return;

    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      autocompleteInputRef.current,
      { types: ['geocode'] }
    );

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (!place.geometry) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const comps = place.address_components || [];
      const find = (types, short) => {
        const c = comps.find(comp => types.some(t => comp.types.includes(t)));
        return c ? (short ? c.short_name : c.long_name) : '';
      };

      setForm(prev => ({
        ...prev,
        center_lat: lat,
        center_lng: lng,
        city: find(['locality', 'administrative_area_level_2']),
        state: find(['administrative_area_level_1'], true),
        zipcode: find(['postal_code']),
        name: prev.name || find(['locality', 'administrative_area_level_2']) || place.formatted_address
      }));
    });
  }, []);

  const updateMap = useCallback(() => {
    if (!mapRef.current || !window.google) return;
    const { center_lat, center_lng, area_type, radius_miles } = form;
    if (!center_lat || !center_lng) return;

    const center = { lat: center_lat, lng: center_lng };

    // Initialize or reuse map
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 10,
        styles: [{ elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
                 { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
                 { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] }]
      });
    } else {
      mapInstanceRef.current.setCenter(center);
    }

    // Clear old overlays
    if (markerRef.current) { markerRef.current.setMap(null); markerRef.current = null; }
    if (circleRef.current) { circleRef.current.setMap(null); circleRef.current = null; }

    // Add marker
    markerRef.current = new window.google.maps.Marker({ position: center, map: mapInstanceRef.current });

    if (area_type === 'city_radius' && radius_miles) {
      circleRef.current = new window.google.maps.Circle({
        map: mapInstanceRef.current,
        center,
        radius: radius_miles * 1609.344,
        fillColor: '#F69620',
        fillOpacity: 0.15,
        strokeColor: '#F69620',
        strokeWeight: 2
      });
      mapInstanceRef.current.fitBounds(circleRef.current.getBounds());
    } else if (area_type === 'zipcode') {
      mapInstanceRef.current.setZoom(13);
    } else if (area_type === 'state') {
      mapInstanceRef.current.setZoom(6);
    }
  }, [form]);

  useEffect(() => {
    if (showForm && form.center_lat && form.center_lng) {
      loadGoogleMaps(() => {
        updateMap();
        initAutocomplete();
      });
    }
  }, [showForm, form.center_lat, form.center_lng, form.area_type, form.radius_miles, loadGoogleMaps, updateMap, initAutocomplete]);

  useEffect(() => {
    if (showForm) {
      loadGoogleMaps(() => initAutocomplete());
    }
  }, [showForm, loadGoogleMaps, initAutocomplete]);

  // ─── Handlers ───────────────────────────────────────────────────────
  const handleAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    mapInstanceRef.current = null;
    autocompleteRef.current = null;
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      area_type: item.area_type,
      center_lat: item.center_lat,
      center_lng: item.center_lng,
      radius_miles: item.radius_miles || 25,
      zipcode: item.zipcode || '',
      city: item.city || '',
      state: item.state || 'MA',
      is_active: item.is_active
    });
    mapInstanceRef.current = null;
    autocompleteRef.current = null;
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    mapInstanceRef.current = null;
    autocompleteRef.current = null;
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await updateServiceArea(editingId, form);
      } else {
        await createServiceArea(form);
      }
      await loadItems();
      handleCancel();
    } catch (err) {
      console.error('Error saving service area:', err);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteServiceArea(deleteId);
      setItems(items.filter(i => i.id !== deleteId));
      setShowModal(false);
    } catch (err) {
      console.error('Error deleting service area:', err);
    }
  };

  const handleTypeChange = (newType) => {
    setForm(prev => ({ ...prev, area_type: newType }));
  };

  const handleStateSelect = (stateCode) => {
    const stateName = US_STATES.find(s => s.code === stateCode)?.name || stateCode;
    setForm(prev => ({
      ...prev,
      state: stateCode,
      name: prev.name || stateName,
      // Clear coordinates — backend will geocode on save
      center_lat: null,
      center_lng: null
    }));

    // Geocode to show on map
    if (window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: `${stateName}, USA` }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const lat = results[0].geometry.location.lat();
          const lng = results[0].geometry.location.lng();
          setForm(prev => ({ ...prev, center_lat: lat, center_lng: lng }));
        }
      });
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // ─── Render ─────────────────────────────────────────────────────────
  if (loading) {
    return <DashboardLayout><Container><p style={{ color: '#ccc' }}>Loading...</p></Container></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <PageTitle><FaMapMarkerAlt /> Service Areas</PageTitle>
          <AddButton onClick={handleAdd}><FaPlus /> Add Area</AddButton>
        </Header>

        {showForm && (
          <FormOverlay>
            <FormTitle>{editingId ? 'Edit Service Area' : 'New Service Area'}</FormTitle>

            <TypeButtons>
              <TypeButton active={form.area_type === 'zipcode'} onClick={() => handleTypeChange('zipcode')}>
                <FaMailBulk /> Zipcode Only
              </TypeButton>
              <TypeButton active={form.area_type === 'city_radius'} onClick={() => handleTypeChange('city_radius')}>
                <FaCity /> City + Radius
              </TypeButton>
              <TypeButton active={form.area_type === 'state'} onClick={() => handleTypeChange('state')}>
                <FaGlobe /> Entire State
              </TypeButton>
            </TypeButtons>

            <FormRow>
              <div>
                <Label>Area Name</Label>
                <Input value={form.name} onChange={e => handleChange('name', e.target.value)} placeholder="e.g. Leominster Area" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.is_active ? 'true' : 'false'} onChange={e => handleChange('is_active', e.target.value === 'true')}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Select>
              </div>
            </FormRow>

            {form.area_type === 'state' ? (
              <div style={{ marginBottom: '12px' }}>
                <Label>Select State</Label>
                <Select value={form.state} onChange={e => handleStateSelect(e.target.value)}>
                  <option value="">-- Select --</option>
                  {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                </Select>
              </div>
            ) : (
              <div style={{ marginBottom: '12px' }}>
                <Label>Search Location (Google Places)</Label>
                <Input
                  ref={autocompleteInputRef}
                  placeholder="Type a city, address, or zip code..."
                  defaultValue=""
                />
              </div>
            )}

            {form.area_type === 'city_radius' && (
              <div style={{ marginBottom: '12px' }}>
                <Label>Service Radius</Label>
                <SliderRow>
                  <RangeInput
                    type="range" min="5" max="100" step="5"
                    value={form.radius_miles}
                    onChange={e => handleChange('radius_miles', parseFloat(e.target.value))}
                  />
                  <RangeValue>{form.radius_miles} mi</RangeValue>
                </SliderRow>
              </div>
            )}

            {form.area_type === 'zipcode' && form.zipcode && (
              <div style={{ marginBottom: '12px' }}>
                <Label>Detected Zipcode</Label>
                <Input value={form.zipcode} readOnly style={{ opacity: 0.7 }} />
              </div>
            )}

            {/* Map preview */}
            {(form.center_lat && form.center_lng) && (
              <MapContainer ref={mapRef} />
            )}

            <ButtonGroup>
              <Button onClick={handleSave}><FaSave /> Save</Button>
              <Button variant="cancel" onClick={handleCancel}><FaTimes /> Cancel</Button>
            </ButtonGroup>
          </FormOverlay>
        )}

        {items.length === 0 ? (
          <EmptyState>No service areas defined. Click "Add Area" to configure where you serve.</EmptyState>
        ) : (
          items.map(item => (
            <AreaCard key={item.id} areaType={item.area_type}>
              <AreaInfo>
                <AreaName>{item.name}</AreaName>
                <AreaMeta>
                  <TypeBadge>{typeIcon(item.area_type)} {typeLabel(item.area_type)}</TypeBadge>
                  {item.area_type === 'zipcode' && item.zipcode && <span>ZIP: {item.zipcode}</span>}
                  {item.area_type === 'city_radius' && <span>{item.city || 'Center'} + {item.radius_miles} mi</span>}
                  {item.area_type === 'state' && item.state && <span>{US_STATES.find(s => s.code === item.state)?.name || item.state}</span>}
                  <ActiveBadge active={item.is_active}>{item.is_active ? 'Active' : 'Inactive'}</ActiveBadge>
                </AreaMeta>
              </AreaInfo>
              <div style={{ display: 'flex', gap: '4px' }}>
                <IconButton onClick={() => handleEdit(item)}><FaEdit /></IconButton>
                <IconButton onClick={() => handleDeleteClick(item.id)} color="#fa7f7f"><FaTrash /></IconButton>
              </div>
            </AreaCard>
          ))
        )}

        {showModal && (
          <Modal
            title="Delete Service Area"
            message="Are you sure you want to delete this service area?"
            onConfirm={handleConfirmDelete}
            onCancel={() => setShowModal(false)}
          />
        )}
      </Container>
    </DashboardLayout>
  );
};

export default ServiceAreaManager;
